import React from "react";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";

/**
 * SectionEditor 컴포넌트 - 선택된 섹션의 속성을 편집하는 UI를 제공합니다.
 * @param {Object} props - 컴포넌트 props
 * @param {Object} props.selectedSection - 현재 선택된 섹션 객체
 * @param {Function} props.onUpdateSection - 섹션 업데이트 핸들러 함수
 * @param {Function} props.onDeleteSection - 섹션 삭제 핸들러 함수
 * @param {Array} props.availableFields - 사용 가능한 필드 목록
 * @param {Function} props.onMoveSectionUp - 섹션을 위로 이동하는 핸들러 함수
 * @param {Function} props.onMoveSectionDown - 섹션을 아래로 이동하는 핸들러 함수
 * @param {Function} props.t - 번역 함수
 */
const SectionEditor = ({
  selectedSection,
  onUpdateSection,
  onDeleteSection,
  availableFields = [],
  onMoveSectionUp,
  onMoveSectionDown,
  t = (key) => key, // 기본 번역 함수
}) => {
  // 선택된 섹션이 없으면 안내 메시지 표시
  if (!selectedSection) {
    return <div className="empty-editor-message">{t("selectSection")}</div>;
  }

  const handleTitleChange = (e) => {
    onUpdateSection({ title: e.target.value });
  };

  const handleDependencyToggle = (e) => {
    if (e.target.checked) {
      onUpdateSection({
        dependency: {
          fieldId: "",
          condition: "equals",
          value: "",
        },
      });
    } else {
      onUpdateSection({ dependency: null });
    }
  };

  const handleDependencyChange = (field, value) => {
    onUpdateSection({
      dependency: {
        ...selectedSection.dependency,
        [field]: value,
      },
    });
  };

  const handleDeleteClick = () => {
    if (window.confirm(t("confirmDeleteSection"))) {
      onDeleteSection(selectedSection.id);
    }
  };

  const dependencyConditions = [
    { value: "equals", label: t("equals") },
    { value: "notEquals", label: t("notEquals") },
  ];

  return (
    <div className="section-editor">
      <div className="property-group">
        <div className="property-group-title">{t("sectionSettings")}</div>

        <div className="property-row">
          <div className="property-label">{t("sectionTitle")}</div>
          <input
            type="text"
            className="form-input"
            value={selectedSection.title || ""}
            onChange={handleTitleChange}
            placeholder={t("enterSectionTitle")}
          />
        </div>

        <div className="property-row checkbox-group">
          <input
            type="checkbox"
            id="section-dependency-toggle"
            checked={!!selectedSection.dependency}
            onChange={handleDependencyToggle}
          />
          <label htmlFor="section-dependency-toggle">
            {t("conditionalDisplay")}
          </label>
        </div>

        {selectedSection.dependency && (
          <div className="property-row">
            <div className="property-label">{t("dependsOnField")}</div>
            <input
              list="section-dep-field-list"
              className="form-input"
              value={selectedSection.dependency.fieldId || ""}
              onChange={(e) =>
                handleDependencyChange("fieldId", e.target.value)
              }
            />
            <datalist id="section-dep-field-list">
              {availableFields.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>

            <select
              className="form-input"
              value={selectedSection.dependency.condition || "equals"}
              onChange={(e) =>
                handleDependencyChange("condition", e.target.value)
              }
            >
              {dependencyConditions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>

            <input
              type="text"
              className="form-input"
              placeholder={t("value")}
              value={selectedSection.dependency.value || ""}
              onChange={(e) => handleDependencyChange("value", e.target.value)}
            />
          </div>
        )}

        <div className="property-row">
          <button
            className="btn btn-outline"
            onClick={() => onMoveSectionUp(selectedSection.id)}
          >
            <FaArrowUp /> {t("moveUp")}
          </button>
          <button
            className="btn btn-outline"
            onClick={() => onMoveSectionDown(selectedSection.id)}
          >
            <FaArrowDown /> {t("moveDown")}
          </button>
          <button className="btn btn-danger" onClick={handleDeleteClick}>
            {t("deleteSection")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SectionEditor;
